import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import GlobalHeader from "../../../components/GlobalHeader";
import SnackAlert from "../../../components/SnackAlert";
import useSnack from "../../../hooks/useSnack";
import useApi from "../../../hooks/useApi";
import FormArtista from "./FormArtista";

// El roster de un sello, manager o label: las fichas de los artistas que
// mueve. Reemplaza a "Mis canciones" en estos perfiles, porque acá el
// material no cuelga de la cuenta sino de cada artista.
export default function ViewRoster() {
  const { loadApi, loadingApi } = useApi();
  const { msgSB, openSB, showSnack, typeSB } = useSnack();
  const [artistas, setArtistas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [verInactivos, setVerInactivos] = useState(false);
  const [formAbierto, setFormAbierto] = useState(false);
  const [enEdicion, setEnEdicion] = useState(null);
  const [aDarDeBaja, setADarDeBaja] = useState(null);

  const cargar = useCallback(() => {
    setCargando(true);
    loadApi(
      `artista/roster${verInactivos ? "?incluir_inactivos=1" : ""}`,
      true,
      "get"
    )
      .then((res) => setArtistas(res.data || []))
      .catch((e) => showSnack(e.message, "error"))
      .finally(() => setCargando(false));
  }, [verInactivos]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const abrirAlta = () => {
    setEnEdicion(null);
    setFormAbierto(true);
  };

  const abrirEdicion = (artista) => {
    setEnEdicion(artista);
    setFormAbierto(true);
  };

  const guardar = (form, imagen, presskit, quitarPresskit) => {
    const body = {
      nombre: form.nombre,
      descripcion: form.descripcion,
      pais: form.pais,
      // Van como JSON dentro del multipart: un objeto y un arreglo no
      // sobreviven a FormData de otra forma.
      generos_musicales: JSON.stringify(form.generos_musicales || []),
      redes: JSON.stringify(form.redes || {}),
    };
    if (imagen) body.imagen = imagen;
    if (presskit) body.presskit = presskit;
    // Solo se manda la marca cuando hay que borrarlo: no mandar el archivo
    // significa "no lo toques", igual que con las redes.
    if (quitarPresskit) body.quitar_presskit = "true";

    const esEdicion = !!enEdicion;
    loadApi(
      esEdicion ? `artista/roster/${enEdicion.id}` : "artista/roster",
      true,
      esEdicion ? "put" : "post",
      body,
      true
    )
      .then((res) => {
        showSnack(res.data.message);
        setFormAbierto(false);
        setEnEdicion(null);
        cargar();
      })
      .catch((e) => showSnack(e.message, "error"));
  };

  const darDeBaja = () => {
    loadApi(`artista/roster/${aDarDeBaja.id}`, true, "delete")
      .then((res) => {
        showSnack(res.data.message);
        setADarDeBaja(null);
        cargar();
      })
      .catch((e) => showSnack(e.message, "error"));
  };

  const reactivar = (artista) => {
    loadApi(`artista/roster/${artista.id}`, true, "put", { activo: true }, true)
      .then(() => {
        showSnack(`${artista.nombre} vuelve a estar activo.`);
        cargar();
      })
      .catch((e) => showSnack(e.message, "error"));
  };

  const guardando =
    loadingApi.includes("post__artista/roster") ||
    loadingApi.includes(`put__artista/roster/${enEdicion?.id}`);

  return (
    <>
      <GlobalHeader />
      <Container>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
        >
          <Typography variant="h4" color="primary.main" fontWeight="900">
            MIS ARTISTAS
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={verInactivos}
                  onChange={(e) => setVerInactivos(e.target.checked)}
                />
              }
              label="Ver dados de baja"
            />
            <Button variant="contained" onClick={abrirAlta}>
              + Agregar artista
            </Button>
          </Stack>
        </Stack>

        <Typography variant="body2" color="text.secondary" mt={1}>
          Cada artista que gestionas tiene su propia ficha. Desde adentro le
          cargas su presskit, sus canciones y sus eventos.
        </Typography>

        {!cargando && artistas.length === 0 && (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary">
              Todavía no agregaste ningún artista.
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Agrega al primero para empezar a cargarle material.
            </Typography>
            <Button variant="contained" onClick={abrirAlta}>
              + Agregar artista
            </Button>
          </Box>
        )}

        <Grid container spacing={2} my={2}>
          {artistas.map((artista) => (
            <Grid item xs={12} sm={6} md={4} key={artista.id}>
              <Card
                sx={{
                  height: "100%",
                  // Las fichas dadas de baja se ven, pero apagadas: siguen
                  // ahí por sus envíos históricos, no son material activo.
                  opacity: artista.activo ? 1 : 0.55,
                }}
              >
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                      src={artista.imagen || undefined}
                      variant="rounded"
                      sx={{ width: 64, height: 64 }}
                    >
                      {artista.nombre?.[0]?.toUpperCase()}
                    </Avatar>
                    <Stack flex={1} minWidth={0}>
                      <Typography fontWeight={700} noWrap>
                        {artista.nombre}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {artista.total_canciones} canciones ·{" "}
                        {artista.total_eventos} eventos
                      </Typography>
                      {!artista.activo && (
                        <Chip
                          label="Dado de baja"
                          size="small"
                          sx={{ mt: 0.5, alignSelf: "flex-start" }}
                        />
                      )}
                    </Stack>
                  </Stack>

                  <Stack direction="row" gap={0.5} flexWrap="wrap" mt={1.5}>
                    {(artista.generos_musicales || [])
                      .slice(0, 3)
                      .map((genero) => (
                        <Chip key={genero} label={genero} size="small" />
                      ))}
                    {(artista.generos_musicales || []).length > 3 && (
                      <Chip
                        label={`+${artista.generos_musicales.length - 3}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Stack>

                  <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
                    <Button
                      size="small"
                      component={Link}
                      to={`/artista/roster/${artista.id}`}
                    >
                      Ver ficha
                    </Button>
                    <Button size="small" onClick={() => abrirEdicion(artista)}>
                      Editar
                    </Button>
                    {artista.activo ? (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => setADarDeBaja(artista)}
                      >
                        Dar de baja
                      </Button>
                    ) : (
                      <Button size="small" onClick={() => reactivar(artista)}>
                        Reactivar
                      </Button>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <FormArtista
        abierto={formAbierto}
        artista={enEdicion}
        onCerrar={() => {
          setFormAbierto(false);
          setEnEdicion(null);
        }}
        onGuardar={guardar}
        guardando={guardando}
        showSnack={showSnack}
      />

      <Dialog open={!!aDarDeBaja} onClose={() => setADarDeBaja(null)}>
        <DialogTitle>Dar de baja a {aDarDeBaja?.nombre}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deja de aparecer en tu roster y no se le puede cargar material
            nuevo. Sus canciones, eventos y envíos anteriores se conservan, y
            puedes reactivarlo cuando quieras.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setADarDeBaja(null)}>Cancelar</Button>
          <Button color="error" onClick={darDeBaja}>
            Dar de baja
          </Button>
        </DialogActions>
      </Dialog>

      <SnackAlert openSB={openSB} typeSB={typeSB} msgSB={msgSB} />
    </>
  );
}
